import React, { useState } from 'react';
import { Play, Rocket, Shield, Zap, ArrowRight, Bot, Target, Users, MousePointerClick, TrendingUp, Cpu, Check } from 'lucide-react';
import AuthModal from './AuthModal';

export default function LandingPage({ onLogin }) {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const handleOpenAuth = () => {
    setIsAuthOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#050507] text-[#d1d5db] font-sans selection:bg-[#3b82f6]/30 selection:text-white overflow-x-hidden relative">
      
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        onLoginSuccess={onLogin} 
      />

      {/* Navigation Layer */}
      <nav className="fixed top-0 inset-x-0 h-20 bg-[#050507]/85 backdrop-blur-md border-b border-[#02B6FF]/10 z-40">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
            <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] rounded-xl flex items-center justify-center shadow-lg shadow-[#02B6FF]/25 relative group overflow-hidden">
                    <span className="text-white font-black text-xl tracking-tighter relative z-10">AF</span>
                    <div className="absolute inset-0 bg-[#050507] opacity-0 group-hover:opacity-10 transition-opacity"></div>
                </div>
                <span className="text-white font-bold text-xl tracking-tight hidden sm:block">
                  Affiliate Flow AI 
                  <span className="text-xs text-[#33D5E6] font-mono tracking-widest ml-3 border border-[#02B6FF]/20 bg-[#02B6FF]/10 px-2 py-0.5 rounded-full relative -top-0.5 shadow-[0_0_10px_rgba(2,182,255,0.1)]">
                    PRE-LAUNCH
                  </span>
                </span>
            </div>
            
            <div className="flex items-center space-x-6">
                <button onClick={handleOpenAuth} className="text-sm font-bold text-slate-400 hover:text-[#02B6FF] transition-colors">
                    Partner Login
                </button>
                <button onClick={handleOpenAuth} className="hidden sm:flex items-center text-sm font-bold bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] text-white hover:from-[#33D5E6] hover:to-[#02B6FF] px-5 py-2.5 rounded-full transition-all shadow-[0_0_15px_rgba(2,182,255,0.25)] hover:shadow-[0_0_20px_rgba(2,182,255,0.45)] hover:-translate-y-0.5">
                    Access Engine <ArrowRight size={16} className="ml-2" />
                </button>
            </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 sm:pt-40 sm:pb-24 lg:pb-32 px-6">
        
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[500px] opacity-25 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] blur-[130px] rounded-full mix-blend-screen animate-pulse"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center z-10">
            <div className="inline-flex items-center space-x-2 bg-[#0a0a12]/80 border border-[#02B6FF]/20 rounded-full px-4 py-1.5 mb-8 shadow-2xl backdrop-blur-sm">
                <span className="flex h-2 w-2 rounded-full bg-[#02B6FF] animate-ping absolute"></span>
                <span className="relative flex h-2 w-2 rounded-full bg-[#02B6FF]"></span>
                <span className="text-xs font-bold text-slate-300 tracking-wider">SYSTEM STATUS: NEURAL ENGINE TRAINING Phase 1</span>
            </div>
            
            <h1 className="text-5xl sm:text-7xl font-extrabold text-white tracking-tight mb-8 leading-[1.1]">
                Launch High-Converting Funnels <br className="hidden lg:block"/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] drop-shadow-[0_0_30px_rgba(2,182,255,0.15)]">With One AI Command.</span>
            </h1>

            <p className="text-[18px] text-[#02B6FF] font-semibold text-center mb-4 tracking-[0.01em] drop-shadow-[0_0_10px_rgba(2,182,255,0.15)]">
                The only affiliate tool with an AI Coach that takes complete beginners to positive ROI — step by step.
            </p>
            
            <p className="text-xl sm:text-2xl text-slate-400 max-w-3xl mx-auto mb-16 leading-relaxed">
                The world's first autonomous affiliate marketing engine. We handle the technical heavy lifting, deep tracking, and instant A/B splitting so you can focus on generating traffic.
            </p>

            {/* VSL Section */}
            <div className="relative max-w-5xl mx-auto pb-16 z-20">
                <div className="rounded-[2.5rem] p-2 bg-gradient-to-b from-[#0a0a12] to-[#030306] shadow-2xl shadow-[#2563eb]/5 w-full aspect-video border border-[#3b82f6]/10 hover:border-[#3b82f6]/35 mx-auto relative group overflow-hidden transition-all duration-700">
                   
                   {/* The Video Container */}
                   <div className="w-full h-full bg-[#030306] rounded-[2.2rem] overflow-hidden relative flex items-center justify-center">
                       
                       <video 
                           className="w-full h-full object-cover sm:object-contain relative z-20"
                           controls
                           preload="metadata"
                       >
                           <source src="https://firebasestorage.googleapis.com/v0/b/affiliate-ninja-app-2026.firebasestorage.app/o/VSL%2FAffiliateNinjaVSL.mp4?alt=media&token=9a0453c4-138d-44ec-8fb3-8b8476a431a1" type="video/mp4" />
                           Your browser does not support the video tag.
                       </video>
                       
                   </div>
                   
                   {/* Floating UI Elements for the thumbnail */}
                    <div className="pointer-events-none absolute top-8 left-8 bg-[#0a0a12]/90 backdrop-blur-md border border-[#02B6FF]/20 rounded-xl p-3 flex items-center shadow-lg -rotate-3 transition-transform group-hover:rotate-0 z-30">
                        <Bot className="text-[#33D5E6] mr-2" size={20}/>
                        <span className="text-white font-bold text-sm">AI Coach Active</span>
                    </div>
                    <div className="pointer-events-none absolute bottom-10 right-8 bg-[#0a0a12]/90 backdrop-blur-md border border-[#02B6FF]/20 rounded-xl p-3 flex items-center shadow-lg rotate-6 transition-transform group-hover:rotate-0 z-30">
                        <span className="text-[#33D5E6] font-black text-lg mr-2">+34%</span>
                        <span className="text-slate-300 font-medium text-xs uppercase">Conv. Rate</span>
                    </div>
                </div>
            </div>

            <div className="max-w-md mx-auto bg-[#0a0a12]/60 border border-[#02B6FF]/15 p-6 sm:p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden backdrop-blur-md hover:border-[#02B6FF]/30 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-[#33D5E6]/2 to-[#0A22EE]/2 pointer-events-none"></div>
                <div className="relative z-10 space-y-5 text-left">
                    <div className="text-center space-y-1 mb-2">
                        <h3 className="text-lg font-bold text-white uppercase tracking-wider">GET NOTIFIED ON LAUNCH</h3>
                        <p className="text-xs text-[#33D5E6] font-bold uppercase tracking-widest text-[10px]">GET THE BEST DEAL AT LAUNCH</p>
                    </div>
                    
                    <form 
                        action="https://mailerxp.com/api/optin" 
                        method="POST"
                        target="mailerxp_iframe"
                        onSubmit={() => {
                            setIsSuccessOpen(true);
                        }}
                        className="space-y-4"
                    >
                        <input type="hidden" name="userId" value="MmSUPC5FNwN4ybgoFJFFD2Vjjpn1" />
                        <input type="hidden" name="listId" value="Gk4MQ4ZJPNHoQQU1oHUO" />
                        <input type="hidden" name="tags" value="beta" />
                        
                        <div className="space-y-1.5">
                            <label className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">First Name</label>
                            <input 
                                type="text" 
                                name="firstName" 
                                required 
                                placeholder="Your Name" 
                                className="w-full bg-[#030306]/80 border border-[#02B6FF]/15 text-white font-bold text-sm px-4 py-3 rounded-2xl focus:border-[#02B6FF] focus:ring-1 focus:ring-[#02B6FF]/20 outline-none transition-all shadow-inner placeholder:text-slate-700" 
                            />
                        </div>
                        
                        <div className="space-y-1.5">
                            <label className="text-[10px] text-slate-400 font-black uppercase tracking-widest block">Email Address</label>
                            <input 
                                type="email" 
                                name="email" 
                                required 
                                placeholder="you@example.com" 
                                className="w-full bg-[#030306]/80 border border-[#02B6FF]/15 text-white font-bold text-sm px-4 py-3 rounded-2xl focus:border-[#02B6FF] focus:ring-1 focus:ring-[#02B6FF]/20 outline-none transition-all shadow-inner placeholder:text-slate-700" 
                            />
                        </div>
                        
                        <button type="submit" className="w-full bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] hover:from-[#33D5E6] hover:to-[#02B6FF] text-white font-black text-sm py-4 rounded-2xl shadow-xl shadow-[#02B6FF]/20 hover:scale-[1.01] active:scale-98 transition-all cursor-pointer flex items-center justify-center gap-2 uppercase tracking-wider font-mono">
                            <span>🔔 Notify Me On Launch →</span>
                        </button>
                    </form>
                    
                    <div className="text-center text-[11px] text-white/50 italic font-medium pt-1">
                        🎁 Launch week subscribers get exclusive early-bird pricing
                    </div>
                    
                    <div className="mt-4 flex items-center justify-center space-x-6 border-t border-[#02B6FF]/10 pt-4">
                        <button onClick={handleOpenAuth} className="text-slate-500 hover:text-[#02B6FF] font-bold text-xs transition-colors border-b border-transparent hover:border-[#02B6FF] uppercase tracking-wider">Partner Login</button>
                        <div className="w-1.5 h-1.5 bg-[#02B6FF]/25 rounded-full"></div>
                        <p className="text-slate-600 font-bold text-[9px] uppercase tracking-widest">POWERED BY MAILCHIMP</p>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* --- FEATURES GRID --- */}
      <div className="relative max-w-7xl mx-auto px-6 py-24 z-20 border-t border-[#02B6FF]/10">
        <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">Unfair Advantages. <span className="text-[#02B6FF]">Built In.</span></h2>
            <p className="text-slate-400 text-lg sm:text-xl max-w-2xl mx-auto">Stop wasting time trying to glue together 5 different expensive SaaS platforms. Affiliate Flow AI is a unified engine designed purely for conversion.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-[#0a0a12]/40 border border-[#02B6FF]/10 p-8 rounded-[2rem] hover:bg-[#0a0a12] hover:border-[#02B6FF]/30 hover:shadow-[0_0_30px_rgba(2,182,255,0.03)] transition duration-300 shadow-xl group">
                <div className="w-14 h-14 bg-[#02B6FF]/10 text-[#33D5E6] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(2,182,255,0.1)]">
                    <Cpu size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Your Bridge Page Builds Itself</h3>
                <p className="text-slate-400 leading-relaxed">Paste your affiliate link. The AI reads the offer page and instantly builds a high-converting bridge page matched to that specific product — copy, layout, and angle included.</p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-[#0a0a12]/40 border border-[#02B6FF]/10 p-8 rounded-[2rem] hover:bg-[#0a0a12] hover:border-[#02B6FF]/30 hover:shadow-[0_0_30px_rgba(2,182,255,0.03)] transition duration-300 shadow-xl group">
                <div className="w-14 h-14 bg-[#0A22EE]/10 text-[#33D5E6] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(10,34,238,0.1)]">
                    <TrendingUp size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Kills Losers. Keeps Winners. Automatically.</h3>
                <p className="text-slate-400 leading-relaxed">The Coach runs your A/B tests in the right order — offer first, then angle, then creative. No more guessing what to test. When a winner is confirmed at 95% confidence, Ninja tells you to scale.</p>
            </div>

            {/* Feature 3 */}
            <div className="bg-[#0a0a12]/40 border border-[#02B6FF]/10 p-8 rounded-[2rem] hover:bg-[#0a0a12] hover:border-[#02B6FF]/30 hover:shadow-[0_0_30px_rgba(2,182,255,0.03)] transition duration-300 shadow-xl group">
                <div className="w-14 h-14 bg-emerald-500/10 text-emerald-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                    <Target size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Know Exactly Which Ad Made You Money</h3>
                <p className="text-slate-400 leading-relaxed">Server-to-server postback tracking follows every click from ad to conversion. You'll know which traffic source, which ad, and which angle generated each sale — down to the cent.</p>
            </div>
            
            {/* Feature 4 */}
            <div className="bg-[#0a0a12]/40 border border-[#02B6FF]/10 p-8 rounded-[2rem] hover:bg-[#0a0a12] hover:border-[#02B6FF]/30 hover:shadow-[0_0_30px_rgba(2,182,255,0.03)] transition duration-300 shadow-xl group">
                <div className="w-14 h-14 bg-[#02B6FF]/10 text-[#33D5E6] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(2,182,255,0.1)]">
                    <Zap size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Pages That Load Before They Click Away</h3>
                <p className="text-slate-400 leading-relaxed">Every funnel is hosted on our global edge network. No WordPress, no plugins, no slow servers. Your bridge pages load instantly — because a 1-second delay kills 7% of your conversions.</p>
            </div>

            {/* Feature 5 */}
            <div className="bg-[#0a0a12]/40 border border-[#02B6FF]/10 p-8 rounded-[2rem] hover:bg-[#0a0a12] hover:border-[#02B6FF]/30 hover:shadow-[0_0_30px_rgba(2,182,255,0.03)] transition duration-300 shadow-xl group">
                <div className="w-14 h-14 bg-[#0A22EE]/10 text-[#33D5E6] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(10,34,238,0.1)]">
                    <Users size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Turn Your Buyers Into Your Affiliates</h3>
                <p className="text-slate-400 leading-relaxed">One click generates a share link that recreates your entire winning funnel in any new user's account — with their JVZoo link auto-injected. Your downline grows while you sleep.</p>
            </div>

            {/* Feature 6 */}
            <div className="bg-[#0a0a12]/40 border border-[#02B6FF]/10 p-8 rounded-[2rem] hover:bg-[#0a0a12] hover:border-[#02B6FF]/30 hover:shadow-[0_0_30px_rgba(2,182,255,0.03)] transition duration-300 shadow-xl group">
                <div className="w-14 h-14 bg-emerald-500/10 text-emerald-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                    <Bot size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">A Coach That Tells You What to Fix Before You Lose Money</h3>
                <p className="text-slate-400 leading-relaxed">The Ninja AI Coach monitors your campaigns 24/7. It spots bottlenecks, flags dying offers, and tells you exactly what to change — and what order to change it in. No more flying blind.</p>
            </div>
        </div>
      </div>

      {/* --- BEFORE/AFTER COMPARISON SECTION --- */}
      <div className="bg-[#0a0a12] border-y border-[#02B6FF]/10 py-20 px-6 sm:px-10">
        <div className="max-w-[860px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-2">Sound Familiar?</h2>
            <p className="text-base text-slate-400">Most affiliates quit because they're fighting the wrong battles in the wrong order.</p>
          </div>
          
          <div className="relative pt-6 divide-y divide-white/[0.06]">
            {/* Headers */}
            <div className="grid grid-cols-2 pb-4">
              <h3 className="text-[10px] sm:text-xs font-black text-rose-500 uppercase tracking-widest flex items-center gap-1.5 border-b border-rose-500/10 pb-3">
                ❌ WITHOUT AFFILIATE FLOW AI
              </h3>
              <h3 className="text-[10px] sm:text-xs font-black text-[#33D5E6] uppercase tracking-widest flex items-center gap-1.5 border-b border-[#02B6FF]/10 pb-3 pl-4 sm:pl-8">
                ✅ WITH AFFILIATE FLOW AI
              </h3>
            </div>
            
            {/* Rows */}
            <div className="grid grid-cols-2 py-4">
              <div className="text-slate-400/50 text-xs sm:text-sm italic font-medium leading-relaxed flex items-start">
                <span className="text-rose-500 mr-2">❌</span> 5 different tools glued together with duct tape
              </div>
              <div className="text-white text-xs sm:text-sm font-bold leading-relaxed flex items-start pl-4 sm:pl-8">
                <span className="text-[#33D5E6] mr-2">✅</span> Everything in one AI-powered platform
              </div>
            </div>

            <div className="grid grid-cols-2 py-4">
              <div className="text-slate-400/50 text-xs sm:text-sm italic font-medium leading-relaxed flex items-start">
                <span className="text-rose-500 mr-2">❌</span> Guess which ad is working and run out of budget
              </div>
              <div className="text-white text-xs sm:text-sm font-bold leading-relaxed flex items-start pl-4 sm:pl-8">
                <span className="text-[#33D5E6] mr-2">✅</span> Coach tells you exactly what to fix and when
              </div>
            </div>

            <div className="grid grid-cols-2 py-4">
              <div className="text-slate-400/50 text-xs sm:text-sm italic font-medium leading-relaxed flex items-start">
                <span className="text-rose-500 mr-2">❌</span> Burn $500 testing the wrong offer for 3 weeks
              </div>
              <div className="text-white text-xs sm:text-sm font-bold leading-relaxed flex items-start pl-4 sm:pl-8">
                <span className="text-[#33D5E6] mr-2">✅</span> Offer validated in 500 clicks (~$200)
              </div>
            </div>

            <div className="grid grid-cols-2 py-4">
              <div className="text-slate-400/50 text-xs sm:text-sm italic font-medium leading-relaxed flex items-start">
                <span className="text-rose-500 mr-2">❌</span> No idea why you're losing money every day
              </div>
              <div className="text-white text-xs sm:text-sm font-bold leading-relaxed flex items-start pl-4 sm:pl-8">
                <span className="text-[#33D5E6] mr-2">✅</span> Bottleneck identified and fixed in minutes
              </div>
            </div>

            <div className="grid grid-cols-2 py-4 border-b border-white/[0.06]">
              <div className="text-slate-400/50 text-xs sm:text-sm italic font-medium leading-relaxed flex items-start">
                <span className="text-rose-500 mr-2">❌</span> Give up after 3 months with nothing to show
              </div>
              <div className="text-white text-xs sm:text-sm font-bold leading-relaxed flex items-start pl-4 sm:pl-8">
                <span className="text-[#33D5E6] mr-2">✅</span> First positive ROI day in under 30 days
              </div>
            </div>

            {/* Vertical Divider line */}
            <div className="absolute top-0 bottom-0 left-1/2 w-[1px] bg-white/[0.08] -translate-x-1/2 pointer-events-none"></div>
          </div>
        </div>
      </div>

      {/* --- SOCIAL PROOF SECTION --- */}
      <div className="relative max-w-7xl mx-auto px-6 py-24 z-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight mb-4">Real Campaigns. Real Results.</h2>
          <p className="text-slate-400 text-lg sm:text-xl max-w-2xl mx-auto">Here's what the AI Coach found in real affiliate funnels.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Card 1 */}
          <div className="bg-[#0a0a12] border border-[#02B6FF]/10 p-8 rounded-2xl shadow-xl hover:border-[#02B6FF]/30 transition-all duration-300">
            <div className="text-5xl font-black text-[#33D5E6] mb-4 drop-shadow-[0_0_15px_rgba(2,182,255,0.3)]">+34%</div>
            <div className="text-[13px] text-slate-400 leading-relaxed">Average conv rate lift after Coach A/B test</div>
          </div>
          {/* Card 2 */}
          <div className="bg-[#0a0a12] border border-[#02B6FF]/10 p-8 rounded-2xl shadow-xl hover:border-[#02B6FF]/30 transition-all duration-300">
            <div className="text-5xl font-black text-[#33D5E6] mb-4 drop-shadow-[0_0_15px_rgba(2,182,255,0.3)]">500</div>
            <div className="text-[13px] text-slate-400 leading-relaxed">Clicks to validate any offer Phase 1 validation</div>
          </div>
          {/* Card 3 */}
          <div className="bg-[#0a0a12] border border-[#02B6FF]/10 p-8 rounded-2xl shadow-xl hover:border-[#02B6FF]/30 transition-all duration-300">
            <div className="text-5xl font-black text-[#33D5E6] mb-4 drop-shadow-[0_0_15px_rgba(2,182,255,0.3)]">$0.45</div>
            <div className="text-[13px] text-slate-400 leading-relaxed">Average CPC on winning native campaigns</div>
          </div>
        </div>

        {/* Testimonial Placeholder */}
        <div className="max-w-[640px] mx-auto bg-[#0a0a12]/60 border border-[#02B6FF]/20 p-8 rounded-3xl text-center shadow-2xl hover:border-[#02B6FF]/40 transition-all duration-300">
          <p className="text-lg italic text-white leading-relaxed mb-4">
            "Finally a tool that tells me WHAT to do, not just shows me data I don't understand."
          </p>
          <p className="text-[13px] font-bold text-[#33D5E6] uppercase tracking-wider font-mono">
            — Beta tester, finance vertical, MaxBounty
          </p>
        </div>
      </div>

      {/* --- HOW IT WORKS / FINAL CTA --- */}
      <div className="relative border-t border-[#02B6FF]/10 bg-[#030306]/80">
        <div className="max-w-4xl mx-auto px-6 py-32 text-center relative z-20">
            <Bot className="text-[#33D5E6] w-16 h-16 mx-auto mb-6 opacity-80 filter drop-shadow-[0_0_15px_rgba(2,182,255,0.3)]" />
            <h2 className="text-4xl sm:text-6xl font-black text-white tracking-tight mb-8">Stop guessing. <br className="hidden sm:block"/><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#33D5E6] to-[#02B6FF] drop-shadow-[0_0_30px_rgba(2,182,255,0.15)]">Start printing ROI.</span></h2>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
                Built by affiliate marketers who've spent years and millions of dollars learning what works — and what doesn't. Everything we know is now your AI Coach. The only question is: are you ready to stop guessing?
            </p>
            
            <button onClick={handleOpenAuth} className="group relative inline-flex items-center justify-center bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] text-white font-black text-xl px-12 py-6 rounded-full overflow-hidden transition-all hover:scale-105 shadow-[0_0_25px_rgba(2,182,255,0.35)] hover:shadow-[0_0_35px_rgba(2,182,255,0.55)] border border-[#02B6FF]/20">
                <span className="relative z-10 flex items-center uppercase tracking-widest font-mono">
                    Access The Neural Engine <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </button>
            <p className="mt-6 text-xs text-white/40 font-mono tracking-wide">
                🔒  Private Alpha  ·  Limited spots  ·  No credit card required to apply
            </p>
        </div>
      </div>

      {/* Success Optin Modal */}
      {isSuccessOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#0a0a12] border border-[#02B6FF]/20 rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl relative text-center animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-gradient-to-br from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#02B6FF]/20 ring-4 ring-[#02B6FF]/10">
              <Check size={32} className="text-white" />
            </div>

            <h2 className="text-3xl font-black text-white mb-2 tracking-tight">
              Access Secured!
            </h2>
            <p className="text-xs text-[#33D5E6] font-bold uppercase tracking-widest mb-6">
              You are on the VIP Waitlist
            </p>

            <p className="text-[#d1d5db] mb-8 text-sm leading-relaxed">
              Success! Your spot has been reserved. We've logged your entry under our <span className="text-[#33D5E6] font-bold">pre-launch waitlist</span>. Keep a close eye on your inbox for upcoming system updates and early access invitations.
            </p>

            <button 
              onClick={() => setIsSuccessOpen(false)}
              className="w-full py-4 bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] hover:from-[#33D5E6] hover:to-[#02B6FF] text-white rounded-xl font-bold text-md shadow-lg shadow-[#02B6FF]/25 transition-all active:scale-95 flex items-center justify-center gap-2 cursor-pointer uppercase tracking-wider font-mono"
            >
              <span>Perfect, Let's Go</span>
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Hidden Iframe for Silent MailerXP Submission */}
      <iframe name="mailerxp_iframe" className="hidden" style={{ display: 'none' }} />

    </div>
  );
}
